/**
 * Rate Limit Handler with Exponential Backoff
 * Handles API rate limiting gracefully with retry logic
 */

interface RateLimitCache {
  resetTime: number;
  retryAfter: number;
}

const rateLimitCache = new Map<string, RateLimitCache>();

interface RetryOptions {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
  silent?: boolean;
}

/**
 * Execute a function with automatic retry on rate limit errors
 */
export async function withRateLimitHandling<T>(
  fn: () => Promise<T>,
  cacheKey: string,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 60000,
    backoffMultiplier = 2,
    silent = false
  } = options;

  // Check if we're in a known rate limit period
  const cached = rateLimitCache.get(cacheKey);
  if (cached && Date.now() < cached.resetTime) {
    const waitTime = cached.resetTime - Date.now();
    if (!silent) {
      console.log(`[Rate Limit] Waiting ${Math.ceil(waitTime / 1000)}s before retry for ${cacheKey}`);
    }
    await sleep(waitTime);
    rateLimitCache.delete(cacheKey);
  }

  let lastError: any;
  let delay = initialDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await fn();
      // Success - clear any cached rate limit
      rateLimitCache.delete(cacheKey);
      return result;
    } catch (error: any) {
      lastError = error;

      // Check if it's a rate limit error
      if (isRateLimitError(error)) {
        const retryAfter = getRetryAfter(error);
        const resetTime = Date.now() + retryAfter;

        // Cache the rate limit info
        rateLimitCache.set(cacheKey, {
          resetTime,
          retryAfter
        });

        if (attempt < maxRetries) {
          const waitTime = Math.min(retryAfter || delay, maxDelay);
          if (!silent) {
            console.log(`[Rate Limit] Attempt ${attempt + 1}/${maxRetries + 1} failed. Waiting ${Math.ceil(waitTime / 1000)}s...`);
          }
          await sleep(waitTime);
          delay = Math.min(delay * backoffMultiplier, maxDelay);
          continue;
        }
      } else {
        // Not a rate limit error - throw immediately
        throw error;
      }
    }
  }

  // All retries exhausted
  throw lastError;
}

/**
 * Check if error is a rate limit error
 */
function isRateLimitError(error: any): boolean {
  if (!error) return false;
  
  // Check status code
  if (error.status === 429 || error.code === 429) return true;
  
  // Check error code/name
  if (error.code === 'RATE_LIMIT_EXCEEDED' || error.code === 'NETWORK_ERROR') {
    return error.status === 429;
  }
  
  if (error.name === 'BlinkNetworkError' && error.status === 429) return true;
  
  // Check message
  const message = error.message || '';
  return message.toLowerCase().includes('rate limit');
}

/**
 * Extract retry-after duration from error (in milliseconds)
 */
function getRetryAfter(error: any): number {
  // Check error details
  if (error.details?.reset) {
    const resetTime = new Date(error.details.reset).getTime();
    const now = Date.now();
    return Math.max(resetTime - now, 1000);
  }
  
  // Check for retry-after header value
  if (error.details?.retryAfter) {
    return error.details.retryAfter * 1000;
  }
  
  // Default to 60 seconds
  return 60000;
}

/**
 * Sleep helper
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Clear all rate limit cache (useful after successful operations)
 */
export function clearRateLimitCache(): void {
  rateLimitCache.clear();
}

/**
 * Get cached rate limit info for debugging
 */
export function getRateLimitInfo(cacheKey: string): RateLimitCache | null {
  return rateLimitCache.get(cacheKey) || null;
}
